import { useCallback, useEffect, useRef } from 'react';

interface AutosaveOptions {
  /** Debounce before a queued save runs (Angular used 600, 700 and 800 ms on different screens). */
  delayMs: number;
  /**
   * Does the save. Read the latest state from refs inside it; it is called after the debounce, and compares its own
   * snapshot with the last saved one to skip a no-op (that comparison is screen specific, so it stays with the screen).
   */
  persist: () => Promise<unknown> | void;
  /**
   * 'concat' (Angular `concatMap`, and the trial balance's promise queue): saves run strictly one after another.
   * 'switch' (Angular `switchMap`): a save starts as soon as its debounce fires, even while an earlier one is in flight.
   */
  mode?: 'concat' | 'switch';
  /** On unmount: save if a change is pending ('pending'), or always ('always'), or never. */
  flushOnUnmount?: 'pending' | 'always' | 'never';
}

export interface Autosave {
  /** Something changed: (re)start the debounce. */
  queueSave: () => void;
  /** Run a task after every save already queued (Angular's `enqueue`, used by explicit Save/Close/Import). */
  enqueue: <T>(task: () => Promise<T> | T) => Promise<T>;
  /** Save now if a change is pending. */
  flush: () => Promise<unknown>;
  /** Whether a change is waiting for its debounce. */
  isPending: () => boolean;
  /** Drop the waiting debounce WITHOUT saving (Angular `clearTimeout(autosaveTimer)`); the change stays unsaved. */
  cancel: () => void;
}

/** The autosave machinery shared by the report settings, company info, accountant report and trial balance editor. */
export function useAutosave({
  delayMs,
  persist,
  mode = 'concat',
  flushOnUnmount = 'pending',
}: AutosaveOptions): Autosave {
  const persistRef = useRef(persist);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef(false);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const optionsRef = useRef({ mode, flushOnUnmount });

  useEffect(() => {
    persistRef.current = persist;
    optionsRef.current = { mode, flushOnUnmount };
  });

  const enqueue = useCallback(<T>(task: () => Promise<T> | T): Promise<T> => {
    const run = chain.current.then(task, task);
    // A failed save must not stop the queue.
    chain.current = run.catch(() => undefined);
    return run as Promise<T>;
  }, []);

  const runPersist = useCallback((): Promise<unknown> => {
    pending.current = false;
    if (optionsRef.current.mode === 'switch') {
      return Promise.resolve(persistRef.current()).catch(() => undefined);
    }
    return enqueue(() => persistRef.current());
  }, [enqueue]);

  const queueSave = useCallback(() => {
    pending.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void runPersist();
    }, delayMs);
  }, [delayMs, runPersist]);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    pending.current = false;
  }, []);

  const flush = useCallback((): Promise<unknown> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    return pending.current ? runPersist() : Promise.resolve();
  }, [runPersist]);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      const when = optionsRef.current.flushOnUnmount;
      if (when === 'always' || (when === 'pending' && pending.current)) {
        void runPersist();
      }
    },
    [runPersist],
  );

  return { queueSave, enqueue, flush, cancel, isPending: () => pending.current };
}
